import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";

const PAGE_SIZE = 10;

// ---------- safe helpers ----------
const safeText = (v) => (v === null || v === undefined ? "" : String(v));
const safeTextDash = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const lower = (v) => safeText(v).toLowerCase();

const safeId = (objOrId) => {
  if (!objOrId) return "";
  if (typeof objOrId === "string") return objOrId;
  return String(objOrId?._id || objOrId?.id || "");
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

// ---------- pickers ----------
const pickUserName = (r) => r?.userId?.name || r?.user?.name || r?.userName || "";
const pickUserEmail = (r) => r?.userId?.email || r?.user?.email || r?.userEmail || "";
const pickUserMobile = (r) => r?.userId?.mobile || r?.user?.mobile || r?.userMobile || "";

const pickSellerName = (r) => r?.sellerId?.name || r?.seller?.name || r?.sellerName || "";
const pickSellerMobile = (r) => r?.sellerId?.mobile || r?.seller?.mobile || r?.sellerMobile || "";

const pickProductName = (r) => r?.productId?.name || r?.product?.name || r?.productName || "";
const pickProductPrice = (r) => r?.productId?.price ?? r?.product?.price ?? r?.price ?? null;
const pickSaleType = (r) => r?.productId?.saleType || r?.product?.saleType || r?.saleType || "";

const pickCategoryName = (r) =>
  r?.productId?.category?.name ||
  r?.product?.category?.name ||
  r?.category?.name ||
  r?.categoryName ||
  "";

const pickSubcategoryName = (r) =>
  r?.productId?.subcategory?.name ||
  r?.product?.subcategory?.name ||
  r?.subcategory?.name ||
  r?.subcategoryName ||
  "";

// ---------- TOKEN ----------
const getAnyToken = () =>
  localStorage.getItem("adminToken") ||
  localStorage.getItem("token") ||
  (() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
    } catch {
      return "";
    }
  })();

// ---------- VALIDATION (MAIN LOGIC) ----------
const isValidRow = (r) => {
  return (
    pickUserName(r)?.trim() &&
    pickUserEmail(r)?.includes("@") &&
    pickUserMobile(r)?.length >= 10 &&
    pickProductName(r)?.trim() &&
    pickSellerName(r)?.trim() &&
    pickCategoryName(r)?.trim()
  );
};

const InterestedUsersDataPage = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  // ---------- FETCH ----------
  useEffect(() => {
    const fetchInterested = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getAnyToken();
        if (!token) {
          setRows([]);
          setError("Token not found. Please login again.");
          return;
        }

        const res = await axios.get(`${baseurl}/api/admin/interested-buyer`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.rows)
          ? res.data.rows
          : [];

        // ✅ CLEAN DATA FILTER
        const cleanedData = data.filter(isValidRow);

        setRows(cleanedData);
      } catch (err) {
        console.error("Fetch error:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to fetch data.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInterested();
  }, []);

  // ---------- SEARCH ----------
  const filtered = useMemo(() => {
    const q = lower(search.trim());
    if (!q) return rows;

    return rows.filter((r) => {
      return (
        lower(pickUserName(r)).includes(q) ||
        lower(pickUserEmail(r)).includes(q) ||
        lower(pickProductName(r)).includes(q) ||
        lower(pickSellerName(r)).includes(q) ||
        lower(pickCategoryName(r)).includes(q) ||
        lower(pickSubcategoryName(r)).includes(q) ||
        lower(r?.location).includes(q)
      );
    });
  }, [rows, search]);

  useEffect(() => setPage(1), [search]);

  // ---------- PAGINATION ----------
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ---------- LOADING ----------
  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Interested Users</h1>

      {error && <div className="text-red-500 mb-3">{error}</div>}

      <input
        type="text"
        placeholder="Search..."
        className="mb-4 w-full p-2 border rounded"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <table className="w-full border">
        <thead>
          <tr className="bg-gray-100">
            <th>User</th>
            <th>Product</th>
            <th>Seller</th>
            <th>Location</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>
          {pageItems.map((r, i) => (
            <tr key={i} className="border-t text-center">
              <td>{safeTextDash(pickUserName(r))}</td>
              <td>{safeTextDash(pickProductName(r))}</td>
              <td>{safeTextDash(pickSellerName(r))}</td>
              <td>{safeTextDash(r?.location)}</td>
              <td>{dtIN(r?.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="mt-4 flex justify-between">
        <button onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
          Prev
        </button>

        <span>
          {page} / {totalPages}
        </span>

        <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
};

export default InterestedUsersDataPage;